import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Download, Trash2, Eye, Upload } from 'lucide-react';
import { BackupItem, RestoreOptions } from './types';
import { formatFileSize, formatDate, getOperationColor, getStatusIcon } from './utils';

interface BackupTableViewProps {
  backups: BackupItem[];
  selectedBackups: string[];
  onBackupSelect: (backupId: string) => void;
  onRestoreBackup: (backupId: string, options: RestoreOptions) => Promise<void>;
}

const BackupTableView: React.FC<BackupTableViewProps> = ({
  backups,
  selectedBackups,
  onBackupSelect,
  onRestoreBackup,
}) => {
  const handleDownload = async (backupId: string, zoneName: string) => {
    try {
      const response = await fetch(`/api/backups/${backupId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup-${zoneName}-${backupId}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download backup:', error);
    }
  };

  const handleDelete = async (backupId: string) => {
    if (window.confirm('Are you sure you want to delete this backup?')) {
      try {
        const response = await fetch(`/api/backups/${backupId}`, { method: 'DELETE' });
        if (response.ok) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to delete backup:', error);
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedBackups.length === backups.length) {
      // Deselect all
      selectedBackups.forEach((id) => onBackupSelect(id));
    } else {
      // Select all
      backups.forEach((backup) => {
        if (!selectedBackups.includes(backup.id)) {
          onBackupSelect(backup.id);
        }
      });
    }
  };

  return (
    <div className="hidden md:block bg-white dark:bg-slate-800 shadow-sm rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectedBackups.length === backups.length}
                  onChange={handleSelectAll}
                  className="rounded border-slate-300 dark:border-slate-600"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Zone
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Operation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Records
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Size
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
            {backups.map((backup) => (
              <motion.tr
                key={backup.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="hover:bg-slate-50 dark:hover:bg-slate-700/50"
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedBackups.includes(backup.id)}
                    onChange={() => onBackupSelect(backup.id)}
                    className="rounded border-slate-300 dark:border-slate-600"
                  />
                </td>
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {backup.zoneName}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {backup.provider}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getOperationColor(backup.operation)}`}
                  >
                    {backup.operation}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(backup.timestamp)}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
                  {backup.recordCount}
                </td>
                <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
                  {formatFileSize(backup.fileSize)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    {getStatusIcon(backup.status)}
                    <span className="ml-2 text-sm text-slate-500 dark:text-slate-400 capitalize">
                      {(backup.status ?? 'unknown').replace('_', ' ')}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(backup.id, backup.zoneName)}
                      className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onRestoreBackup(backup.id, {})}
                      className="p-1 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                      title="Restore"
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(backup.id)}
                      className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BackupTableView;
